import {Service} from '../Service';
import {HistoryEntry} from '../../../common/model/types/history/HistoryEntry';
import {HistoryEntry as ApiHistoryEntry, SortOrder} from '../../rest-client/aat';
import {tAsString} from '../../../common/helpers/react/text/tAsString';

export interface HistoryPage {
    historyEntries: HistoryEntry[];
    page: number;
    pageSize: number;
    totalRecords: number;
}

export class HistoryService extends Service {
    /**
     * Reads the "View history of group and setting changes" report - the very same CM audit log
     * the desktop admin tool shows. The backend caps it at the last 30 days and 1000 entries.
     */
    async getHistory(page: number, pageSize: number, sortBy?: string, sortOrder?: SortOrder): Promise<HistoryPage> {
        const historyApi = await this.services.aat.History;
        const response = await historyApi.getGroupSettingsHistory({page, pageSize, sortBy, sortOrder});

        this.checkStatus(response);

        return {
            historyEntries: response.historyEntries.map(asHistoryEntry),
            page: response.page,
            pageSize: response.pageSize,
            totalRecords: response.totalRecords
        }
    }
}

function asHistoryEntry(entry: ApiHistoryEntry): HistoryEntry {
    return {
        id: asHistoryEntryId(entry),
        date_time: entry.dateTime,
        edited_by_id: entry.editedById,
        edited_by_name: entry.editedByName,
        change_type: asChangeTypeLabel(entry.changeType),
        group_name: entry.groupName,
        agent_name: entry.agentName,
        agent_id: entry.agentId,
        product_name: entry.productName,
        new_setting: entry.newSetting
    };
}

/**
 * Builds a stable row id out of the entry itself, so rows keep their React key when the report
 * is reloaded and new audit entries show up on top. The backend query unions the audit rows,
 * which already discards duplicates, hence the full set of columns identifies an entry.
 */
function asHistoryEntryId(entry: ApiHistoryEntry): string {
    return [
        entry.dateTime,
        entry.editedById,
        entry.editedByName,
        entry.changeType,
        entry.groupName,
        entry.agentName,
        entry.agentId,
        entry.productName,
        entry.newSetting
    ].join('|');
}

/**
 * Change types the CM audit log can report, mapped to their translation keys.
 */
const CHANGE_TYPE_KEYS: Record<string, string> = {
    CREATE: 'CHANGE_TYPE_CREATE',
    UPDATE: 'CHANGE_TYPE_UPDATE',
    DELETE: 'CHANGE_TYPE_DELETE',
    ASSIGN: 'CHANGE_TYPE_ASSIGN',
    UNASSIGN: 'CHANGE_TYPE_UNASSIGN',
    LOCK: 'CHANGE_TYPE_LOCK',
    UNLOCK: 'CHANGE_TYPE_UNLOCK'
};

/**
 * Turns the raw CM operation type (CREATE, UNASSIGN, ...) into a localized label,
 * falling back to the raw value so unknown operation types stay readable.
 */
function asChangeTypeLabel(changeType: string): string {
    if (!changeType) {
        return '';
    }

    const key = CHANGE_TYPE_KEYS[changeType.toUpperCase()];

    return key ? tAsString(key) : changeType;
}
